import React, { useMemo, useState } from 'react';
import { ReactFlow, Handle, Position, Background, Controls } from '@xyflow/react';
import '@xyflow/react/dist/style.css';

// Network topology diagrams (Controls -> Network tab). Data-driven from the
// networkDevices collection: each doc's `parent` field is its uplink, so
// editing Firestore reshapes the diagrams — no code changes. Two sections:
// the wired backbone (internet -> router -> switch -> ethernet devices) and
// the Wi-Fi devices hanging off the router. Every node carries an ETH/Wi-Fi
// badge; each section can pop out to a fullscreen view.

const NODE_W = 168;
const NODE_H = 64;
const GAP_X = 26;
const GAP_Y = 66;
const GRID_COLS = 4; // pack >5 leaf children into rows of 4 instead of one long row

function DeviceNode({ data }) {
  return (
    <div className={`ah-flow-node ${data.planned ? 'planned' : ''} ${data.root ? 'root' : ''}`}>
      <Handle type="target" position={Position.Top} className="ah-flow-handle" />
      {data.conn && (
        <span className={`ah-flow-conn ${data.conn}`}>
          {data.conn === 'wifi' ? 'Wi-Fi' : 'ETH'}
        </span>
      )}
      <div className="ah-flow-name">
        {data.live && (
          <span className={`ah-net-dot ${data.live.ok ? 'ok' : 'down'}`} title={data.live.label} />
        )}
        {data.label}
      </div>
      {data.sub && <div className="ah-flow-sub">{data.sub}</div>}
      <Handle type="source" position={Position.Bottom} className="ah-flow-handle" />
    </div>
  );
}

const nodeTypes = { device: DeviceNode };

// Tidy tree with grid packing: a parent's leaf children wrap into rows of
// GRID_COLS; children with their own subtrees keep classic tidy-tree slots
// to the right of the grid. Returns { id -> {x, y} } in slot units.
function layout(root, childrenOf) {
  const pos = {};
  let slot = 0;

  const place = (dev, depth) => {
    const kids = childrenOf(dev.id);
    if (!kids.length) {
      pos[dev.id] = { x: slot, y: depth };
      slot += 1;
      return;
    }
    const start = slot;
    const leaves = kids.filter((k) => !childrenOf(k.id).length);
    const branches = kids.filter((k) => childrenOf(k.id).length);

    if (kids.length > 5 && leaves.length > 1) {
      leaves.forEach((k, i) => {
        pos[k.id] = { x: start + (i % GRID_COLS), y: depth + 1 + Math.floor(i / GRID_COLS) };
      });
      slot = start + Math.min(GRID_COLS, leaves.length);
      branches.forEach((k) => place(k, depth + 1));
    } else {
      kids.forEach((k) => place(k, depth + 1));
    }
    const xs = kids.map((k) => pos[k.id].x);
    pos[dev.id] = { x: (Math.min(...xs) + Math.max(...xs)) / 2, y: depth };
    slot = Math.max(slot, start + 1);
  };

  place(root, 0);
  return pos;
}

const toPx = ({ x, y }) => ({ x: x * (NODE_W + GAP_X), y: y * (NODE_H + GAP_Y) });

function buildSection(devices, liveness, section) {
  const list = devices || [];
  const byId = Object.fromEntries(list.map((d) => [d.id, d]));
  const root = list.find((d) => !d.parent);
  if (!root) return { nodes: [], edges: [] };

  // Wired: the ethernet tree, with a synthetic internet node above the root.
  // Wi-Fi: the root (as anchor) plus its wifi-connected descendants.
  const included = (d) =>
    section === 'wired' ? (d.conn || 'ethernet') === 'ethernet' : d.conn === 'wifi' || d.id === root.id;

  const childrenOf = (id) =>
    list
      .filter((d) => d.parent === id && included(d))
      .sort((a, b) => (a.order || 0) - (b.order || 0));

  const pos = layout(root, childrenOf);

  const deviceNode = (d) => ({
    id: d.id,
    type: 'device',
    position: toPx(section === 'wired' ? { ...pos[d.id], y: pos[d.id].y + 1 } : pos[d.id]),
    data: {
      label: d.name,
      sub: [d.ip, d.firmware].filter(Boolean).join(' · ') || null,
      conn: d.conn || 'ethernet',
      planned: !!d.planned,
      live: d.planned ? null : liveness(d),
    },
  });

  const shown = list.filter((d) => pos[d.id] !== undefined && included(d));
  const nodes = shown.map(deviceNode);
  const edges = shown
    .filter((d) => d.parent && byId[d.parent] && pos[d.parent] !== undefined)
    .map((d) => ({
      id: `e_${d.id}`,
      source: d.parent,
      target: d.id,
      type: 'smoothstep',
      style:
        d.conn === 'wifi'
          ? { stroke: 'rgba(255,255,255,0.35)', strokeDasharray: '6 4' }
          : { stroke: 'rgba(90,200,250,0.55)' },
    }));

  if (section === 'wired') {
    nodes.unshift({
      id: '_internet',
      type: 'device',
      position: toPx(pos[root.id]),
      data: { label: 'Internet', sub: 'modem / WAN', root: true },
    });
    edges.unshift({
      id: 'e_internet',
      source: '_internet',
      target: root.id,
      type: 'smoothstep',
      style: { stroke: 'rgba(90,200,250,0.55)' },
    });
  }

  return { nodes, edges };
}

function FlowView({ nodes, edges }) {
  return (
    <ReactFlow
      nodes={nodes}
      edges={edges}
      nodeTypes={nodeTypes}
      colorMode="dark"
      fitView
      fitViewOptions={{ padding: 0.08 }}
      minZoom={0.15}
      nodesDraggable={false}
      nodesConnectable={false}
      elementsSelectable={false}
      proOptions={{ hideAttribution: true }}
    >
      <Background gap={24} size={1} />
      <Controls showInteractive={false} />
    </ReactFlow>
  );
}

// Remote access (Tailscale) — a fixed topology, not data-driven: how an
// enrolled device anywhere reaches the LAN through the housebox subnet
// router, and why the public internet can't. Facts mirror
// brain/wiki/systems/home-network.md.
function buildRemoteSection() {
  const node = (id, x, y, data) => ({ id, type: 'device', position: { x, y }, data });
  const edgeLabelProps = {
    labelStyle: { fill: 'rgba(255,255,255,0.75)', fontSize: 10 },
    labelBgStyle: { fill: 'rgba(20,22,28,0.9)' },
  };
  return {
    nodes: [
      node('_you', 60, 0, {
        label: 'Your Mac / phone',
        sub: 'Tailscale signed in · anywhere',
        root: true,
      }),
      node('_public', 470, 0, {
        label: 'Public internet',
        sub: 'no route to 192.168.50.x',
        planned: true,
      }),
      node('_pi', 265, 170, {
        label: 'housebox (Pi 5)',
        sub: 'Tailscale subnet router · 100.74.4.43',
        conn: 'ethernet',
      }),
      node('_router', 40, 340, {
        label: 'ASUS router admin',
        sub: 'http://192.168.50.1 · https :8443',
        conn: 'ethernet',
      }),
      node('_ha', 265, 340, {
        label: 'Home Assistant',
        sub: 'http://192.168.50.184:8123',
        conn: 'ethernet',
      }),
      node('_lan', 490, 340, {
        label: 'Any LAN device',
        sub: '192.168.50.x',
        conn: 'wifi',
      }),
    ],
    edges: [
      {
        id: 'e_tunnel',
        source: '_you',
        target: '_pi',
        type: 'smoothstep',
        animated: true,
        label: 'WireGuard tunnel — end-to-end encrypted',
        style: { stroke: 'rgba(90,200,250,0.8)' },
        ...edgeLabelProps,
      },
      {
        id: 'e_blocked',
        source: '_public',
        target: '_pi',
        type: 'smoothstep',
        label: '✕ blocked — no open ports, LAN invisible',
        style: { stroke: 'rgba(255,90,90,0.55)', strokeDasharray: '4 4' },
        ...edgeLabelProps,
      },
      ...['_router', '_ha', '_lan'].map((t) => ({
        id: `e${t}`,
        source: '_pi',
        target: t,
        type: 'smoothstep',
        style: { stroke: 'rgba(90,200,250,0.55)' },
      })),
    ],
  };
}

const SECTIONS = [
  { key: 'wired', title: 'Wired backbone' },
  { key: 'wifi', title: 'Wi-Fi devices' },
  { key: 'remote', title: 'Remote access (Tailscale)' },
];

export default function NetworkDiagram({ devices, liveness }) {
  const [expanded, setExpanded] = useState(null); // section key or null

  const built = useMemo(
    () =>
      Object.fromEntries(
        SECTIONS.map(({ key }) => [
          key,
          key === 'remote' ? buildRemoteSection() : buildSection(devices, liveness, key),
        ])
      ),
    [devices, liveness]
  );

  if (!devices || !devices.length) return null;

  return (
    <>
      {SECTIONS.map(({ key, title }) => {
        const { nodes, edges } = built[key];
        if (!nodes.length) return null;
        return (
          <div key={key} className="ah-flow-section">
            <div className="ah-flow-head">
              <h2 className="ah-ctl-section">{title}</h2>
              <button
                type="button"
                className="cs-btn cs-btn-outline cs-btn-sm"
                onClick={() => setExpanded(key)}
              >
                Expand
              </button>
            </div>
            <div className="ah-flow-wrap">
              <FlowView nodes={nodes} edges={edges} />
            </div>
            <p className="ah-flow-legend">
              {key === 'remote' ? (
                <>
                  Only devices enrolled in the tailnet can reach the LAN — the
                  public internet has no path in (no open ports, nothing to scan).
                </>
              ) : (
                <>
                  <span className="eth">━━</span> ethernet&ensp;
                  <span className="wifi">╌╌</span> Wi-Fi&ensp;·&ensp;dashed cards are planned
                </>
              )}
            </p>
          </div>
        );
      })}

      {expanded && (
        <div className="ah-flow-modal">
          <div className="ah-flow-modal-head">
            <h2>{SECTIONS.find((s) => s.key === expanded).title}</h2>
            <button
              type="button"
              className="cs-btn cs-btn-outline cs-btn-sm"
              onClick={() => setExpanded(null)}
            >
              Close
            </button>
          </div>
          <div className="ah-flow-modal-body">
            <FlowView nodes={built[expanded].nodes} edges={built[expanded].edges} />
          </div>
        </div>
      )}
    </>
  );
}
